/**
 * `oem:ask` — the one way a scene asks the assistant a question.
 *
 * ## The contract
 *
 * ```ts
 * import { askOem } from '@/versions/v1/shared/assistant/ask';
 * askOem('What does doing nothing cost us today?');
 * ```
 *
 * Dispatches `CustomEvent<string>` named `oem:ask` on `window`. `ChatProvider`
 * listens for it, opens the AI rail if it is closed, and sends the prompt
 * through the same path as a typed message.
 *
 * ## Why an event and not a hook
 *
 * The suggestion chips live on Tier 1 scenes; the assistant lives in the
 * chrome. A window event keeps that one-directional: a scene never imports
 * `useChat`, never reaches into the rail's state, and never has to be mounted
 * inside a provider it does not otherwise need. It also means the Asset360
 * "Today" panel, the per-view `<AskStrip>` and the command palette all go
 * through one entry point, so the behaviour is defined once.
 *
 * Engine layer (layer 2 of the 4-layer architecture) — a side effect on
 * `window`, no derivation and no React.
 *
 * @see src/versions/v1/shared/ChatContext.tsx — the listener
 * @see src/versions/v1/chrome-oem/AskStrip.tsx — the chip row that calls it
 */

/** The event name. */
export const OEM_ASK_EVENT = 'oem:ask';

/**
 * Ask the assistant a question from anywhere in the app.
 *
 * Silently ignores an empty prompt, and is a no-op outside the browser so a
 * server render or a snapshot pass never throws.
 */
export function askOem(prompt: string): void {
  if (typeof window === 'undefined') return;
  const text = prompt.trim();
  if (!text) return;
  window.dispatchEvent(new CustomEvent<string>(OEM_ASK_EVENT, { detail: text }));
}
